import { Readout } from '@saniplus/ui';

import { positionInWindow } from '../lib/format';

/**
 * The 60/60 window as a track, with the refused attempt marked on it.
 *
 * Graft G3 from the losing ladder variant: A's band already says « 48 min ·
 * AVANT L'OUVERTURE », and this repeats the number *beside the rule it
 * violates*, so a worker refused at 20 h 12 learns the rule once instead of
 * trying again at 20 h 20.
 *
 * Drawn **to scale** on the window, which is the defect the vertical
 * night-rail variant was marked down for: a rail that looks like a timeline
 * and is not one is the one place the instrument idiom would lie. The track
 * spans the window; an attempt outside it pins to an edge rather than
 * escaping.
 */
interface PunchWindowRailProps {
  readonly opensAt: string;
  readonly closesAt: string;
  readonly attemptAt: string;
  /** Already translated and formatted: « 21 h 00 », « 23 h 00 », « 20 h 12 ». */
  readonly opensLabel: string;
  readonly closesLabel: string;
  readonly attemptLabel: string;
  /** « votre essai » and « fenêtre ouverte 2 h », already translated. */
  readonly attemptCaption: string;
  readonly windowCaption: string;
  /** The accessible sentence for the whole rail, already translated. */
  readonly railLabel: string;
}

export function PunchWindowRail({
  opensAt,
  closesAt,
  attemptAt,
  opensLabel,
  closesLabel,
  attemptLabel,
  attemptCaption,
  windowCaption,
  railLabel,
}: PunchWindowRailProps) {
  // The track shows an hour of margin on each side of the window so an attempt
  // that lands outside it has somewhere to be drawn.
  const opens = Date.parse(opensAt);
  const closes = Date.parse(closesAt);
  const margin = Number.isNaN(opens) || Number.isNaN(closes) ? 0 : (closes - opens) / 2;
  const trackFrom = new Date(opens - margin).toISOString();
  const trackTo = new Date(closes + margin).toISOString();

  const windowStart = positionInWindow(opensAt, trackFrom, trackTo) ?? 25;
  const windowEnd = positionInWindow(closesAt, trackFrom, trackTo) ?? 75;
  const attempt = positionInWindow(attemptAt, trackFrom, trackTo) ?? 0;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Readout scale="detail" dateTime={opensAt}>
          {opensLabel}
        </Readout>
        <Readout scale="detail" dateTime={closesAt}>
          {closesLabel}
        </Readout>
      </div>

      <div
        role="img"
        aria-label={railLabel}
        className="relative h-2.5 w-full rounded-chip bg-surface-sunk"
      >
        <span
          className="absolute inset-y-0 rounded-chip bg-status-neutral"
          style={{ insetInlineStart: `${String(windowStart)}%`, width: `${String(windowEnd - windowStart)}%` }}
        />
        <span
          data-testid="window-rail-attempt"
          className="absolute inset-y-0 w-[3px] rounded-chip bg-status-warning"
          style={{ insetInlineStart: `${String(attempt)}%` }}
        />
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-body text-status-warning-ink">
          <Readout scale="data" tone="onField" className="me-1 text-status-warning-ink">
            {attemptLabel}
          </Readout>
          {attemptCaption}
        </p>
        <p className="text-body text-ink-muted">{windowCaption}</p>
      </div>
    </div>
  );
}
